/// <reference types="vite/client" />

import { createApiClient, isApiError, getApiError, type ApiClient } from '@aia/platform-core';

export { isApiError, getApiError };

const TOKEN_KEY = 'progresspulse_token';
const USER_KEY = 'progresspulse_user';
const E2E_MODE_KEY = 'e2e_test_mode';

export const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

const baseClient = createApiClient({ baseUrl: API_BASE_URL });

function isE2EMode(): boolean {
  if (typeof window === 'undefined') return false;
  return sessionStorage.getItem(E2E_MODE_KEY) === 'true';
}

export function enableE2EMode(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(E2E_MODE_KEY, 'true');
}

function authHeaders(): Record<string, string> {
  const token =
    typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY)
      : null;
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  if (isE2EMode()) {
    headers['X-E2E-Bypass'] = 'true';
  }
  return headers;
}

const AUTH_PAGES = [
  '/login',
  '/register',
  '/enroll',
  '/forgot-password',
  '/reset-password',
  '/sso',
];

function isOnAuthPage(): boolean {
  if (typeof window === 'undefined') return false;
  return AUTH_PAGES.some((p) => window.location.pathname.startsWith(p));
}

function handleUnauthorized(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  if (!isOnAuthPage()) {
    window.location.href = '/login?session_expired=true';
  }
}

// Short-lived access tokens (15 min) are silently renewed using the HttpOnly
// `aia_refresh_token` cookie (sent via credentials:include to /auth/refresh).
// Concurrent callers share a single in-flight refresh so we never stampede the
// endpoint or rotate the refresh token more than once per expiry.
let refreshPromise: Promise<boolean> | null = null;

export async function refreshAccessToken(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await baseClient.post<{ accessToken: string; refreshToken?: string }>(
          '/auth/refresh',
        );
        const newToken = res.data?.accessToken;
        if (!newToken) return false;
        // Preserve whichever storage held the original token ("remember me").
        if (localStorage.getItem(TOKEN_KEY)) {
          localStorage.setItem(TOKEN_KEY, newToken);
        }
        sessionStorage.setItem(TOKEN_KEY, newToken);
        return true;
      } catch {
        return false;
      }
    })();
    void refreshPromise.finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

interface SkipAuthOptions {
  skipAuthRedirect?: boolean;
}

async function withAuth<T>(fn: () => Promise<T>, opts?: SkipAuthOptions): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (isApiError(err) && err.status === 401 && !isOnAuthPage()) {
      // Attempt a one-time silent refresh, then replay the original request
      // (fn re-reads the freshly stored token from authHeaders()).
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        try {
          return await fn();
        } catch (retryErr) {
          if (!opts?.skipAuthRedirect && isApiError(retryErr) && retryErr.status === 401) {
            handleUnauthorized();
          }
          throw retryErr;
        }
      }
      if (!opts?.skipAuthRedirect) {
        handleUnauthorized();
      }
    }
    throw err;
  }
}

export const api: ApiClient = {
  get: (url, opts) =>
    withAuth(() =>
      baseClient.get(url, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
    ),
  post: (url, body, opts) =>
    withAuth(() =>
      baseClient.post(url, body, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
    ),
  put: (url, body, opts) =>
    withAuth(() =>
      baseClient.put(url, body, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
    ),
  patch: (url, body, opts) =>
    withAuth(() =>
      baseClient.patch(url, body, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
    ),
  delete: (url, opts) =>
    withAuth(() =>
      baseClient.delete(url, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
    ),
};

/**
 * Download an authenticated binary/text response (e.g. CSV export) as a file.
 * The shared ApiClient parses JSON, so file endpoints use a raw fetch here that
 * still carries the bearer token, performs a one-time silent refresh on 401, and
 * honours the server's Content-Disposition filename.
 */
export async function downloadFile(url: string, fallbackFilename: string): Promise<void> {
  const doFetch = () =>
    fetch(`${API_BASE_URL}${url}`, {
      method: 'GET',
      headers: authHeaders(),
      credentials: 'include',
    });

  let res = await doFetch();
  if (res.status === 401 && !isOnAuthPage()) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      res = await doFetch();
    }
    if (res.status === 401) {
      handleUnauthorized();
      throw new Error('Your session has expired. Please sign in again.');
    }
  }
  if (!res.ok) {
    throw new Error(`Download failed (${res.status})`);
  }

  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = match?.[1] ?? fallbackFilename;

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

export const authApi: ApiClient = {
  get: (url, opts) =>
    withAuth(
      () => baseClient.get(url, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
      { skipAuthRedirect: true },
    ),
  post: (url, body, opts) =>
    withAuth(
      () =>
        baseClient.post(url, body, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
      { skipAuthRedirect: true },
    ),
  put: (url, body, opts) =>
    withAuth(
      () => baseClient.put(url, body, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
      { skipAuthRedirect: true },
    ),
  patch: (url, body, opts) =>
    withAuth(
      () =>
        baseClient.patch(url, body, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
      { skipAuthRedirect: true },
    ),
  delete: (url, opts) =>
    withAuth(
      () => baseClient.delete(url, { ...opts, headers: { ...authHeaders(), ...opts?.headers } }),
      { skipAuthRedirect: true },
    ),
};
